import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

/*
  User-side Project Requests list.
  - Shows status + costs for the user's custom translation requests.
  - Allows the user to open the quote view for a specific request.

  Notes:
  - If an approval token exists, we load the quote via /project-approval/{token}.
  - If a request is still in Open Request (quote in process), we render an estimated quote from the list data.
*/
export default function ProjectRequests({ API_BASE }) {
  const { user, jwt } = useAuth();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  const [quoteToken, setQuoteToken] = useState("");
  const [quoteTaskId, setQuoteTaskId] = useState("");
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState("");
  const [showPaymentChoices, setShowPaymentChoices] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [quoteData, setQuoteData] = useState(null);

  // List table sorting
  const [sortKey, setSortKey] = useState("created"); // "project" | "created" | "status" | "action"
  const [sortDir, setSortDir] = useState("desc"); // "asc" | "desc"

  // Table horizontal scroll sync (top + bottom) for long tables.
  const tableScrollWrapRef = useRef(null);
  const tableScrollTopRef = useRef(null);
  const tableRef = useRef(null);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);
  const syncingScrollRef = useRef(false);

  const getQuoteTokenFromUrl = () => {
    try {
      const params = new URLSearchParams(window.location.search || "");
      return params.get("quote") || "";
    } catch {
      return "";
    }
  };

  const setQuoteTokenInUrl = (token) => {
    try {
      const url = new URL(window.location.href);
      if (token) {
        url.searchParams.set("quote", token);
        url.hash = "#project-requests";
      } else {
        url.searchParams.delete("quote");
      }
      window.history.pushState({}, "", url.toString());
    } catch {
      // noop
    }
  };

  const fetchRequests = async () => {
    if (!jwt) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_BASE}/project-requests`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      const data = res?.data || {};
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      console.error("Failed to load project requests:", e);
      setError("Failed to load project requests.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuote = async (token) => {
    if (!token) return;
    setQuoteLoading(true);
    setQuoteError("");
    setQuoteData(null);
    try {
      const res = await axios.get(`${API_BASE}/project-approval/${token}`);
      setQuoteData(res?.data || null);
    } catch (e) {
      console.error("Failed to load quote:", e);
      setQuoteError("Failed to load quote.");
      setQuoteData(null);
    } finally {
      setQuoteLoading(false);
    }
  };

  const submitDecision = async (decision) => {
    if (!quoteToken) return;
    const d = String(decision || "").trim().toLowerCase();
    if (!d) return;

    setQuoteLoading(true);
    setQuoteError("");
    try {
      await axios.post(`${API_BASE}/project-approval/${quoteToken}/decision`, { decision: d });
      // Refresh quote + list
      await fetchQuote(quoteToken);
      await fetchRequests();
    } catch (e) {
      console.error("Failed to submit decision:", e);
      setQuoteError("Failed to submit your decision. Please try again.");
    } finally {
      setQuoteLoading(false);
    }
  };
  const startProjectPaymentStripe = async () => {
    if (!quoteToken || !jwt) return;
    setPaymentError("");
    setQuoteLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/billing/project/checkout/stripe`,
        { approval_token: quoteToken },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      const url = res?.data?.checkout_url || res?.data?.url || "";
      if (!url) throw new Error("Missing checkout_url");
      window.location.href = url;
    } catch (e) {
      console.error("Failed to start Stripe checkout:", e);
      setPaymentError("Failed to start Stripe checkout. Please try again.");
    } finally {
      setQuoteLoading(false);
    }
  };

  const startProjectPaymentPayPal = async () => {
    if (!quoteToken || !jwt) return;
    setPaymentError("");
    setQuoteLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/billing/project/checkout/paypal`,
        { approval_token: quoteToken },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      const approveUrl = res?.data?.approve_url || "";
      if (!approveUrl) throw new Error("Missing approve_url");
      window.location.href = approveUrl;
    } catch (e) {
      console.error("Failed to start PayPal checkout:", e);
      setPaymentError("Failed to start PayPal checkout. Please try again.");
    } finally {
      setQuoteLoading(false);
    }
  };



  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line
  }, [jwt]);

  // Sync quote token from the URL (supports browser back/forward).
  useEffect(() => {
    const syncFromUrl = () => {
      const token = getQuoteTokenFromUrl();
      setQuoteToken(token);
      setShowPaymentChoices(false);
      setPaymentError("");
      if (!token) {
        setQuoteTaskId("");
        setQuoteData(null);
        setQuoteError("");
        setQuoteLoading(false);
      }
    };

    syncFromUrl();

    const onPopState = () => {
      syncFromUrl();
    };

    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
    // eslint-disable-next-line
  }, []);

  // Fetch quote when token changes.
  useEffect(() => {
    if (!quoteToken) return;
    fetchQuote(quoteToken);
    // eslint-disable-next-line
  }, [quoteToken]);

  // Keep the top horizontal scrollbar sized to the table content width.
  useEffect(() => {
    try {
      const el = tableRef.current;
      if (!el) return;
      const update = () => {
        try {
          setTableScrollWidth(el.scrollWidth || 0);
        } catch {
          // noop
        }
      };
      update();

      // Watch for content/viewport changes.
      let ro = null;
      if (typeof ResizeObserver !== "undefined") {
        ro = new ResizeObserver(() => update());
        ro.observe(el);
      } else {
        window.addEventListener("resize", update);
      }

      return () => {
        try {
          if (ro) ro.disconnect();
          else window.removeEventListener("resize", update);
        } catch {
          // noop
        }
      };
    } catch {
      // noop
    }
    // eslint-disable-next-line
  }, [items]);

  const openQuoteForRequest = (req) => {
    if (!req) return;

    // For Cost Submitted (ready for approval), we expect an approval token.
    if (req.approval_token) {
      setQuoteTokenInUrl(req.approval_token);
      setQuoteToken(req.approval_token);
      setQuoteTaskId(req.task_id || "");
      window.scrollTo(0, 0);
      return;
    }

    // For Open Request (quote in process), render an estimated quote from list data.
    setQuoteTokenInUrl("");
    setQuoteToken("");
    setQuoteTaskId(req.task_id || "");
    setQuoteData(null);
    window.scrollTo(0, 0);
  };

  const closeQuote = () => {
    setQuoteTokenInUrl("");
    setQuoteToken("");
    setQuoteTaskId("");
    setQuoteData(null);
    setQuoteError("");
    setQuoteLoading(false);
    window.scrollTo(0, 0);
  };

  const normalizeTimestampForLocalDisplay = (ts) => {
    try {
      const s0 = String(ts || "").trim();
      if (!s0) return "";
      // If the timestamp has an explicit timezone (Z or +/-HH:MM), keep it.
      const hasTz = /[zZ]|[\+\-]\d{2}:?\d{2}$/.test(s0);
      if (hasTz) return s0;

      // If backend returns naive timestamps (no timezone), treat them as UTC.
      // Supports "YYYY-MM-DD HH:MM:SS" and "YYYY-MM-DDTHH:MM:SS".
      if (/^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}(:\d{2})?/.test(s0)) {
        const isoLike = s0.indexOf(" ") !== -1 ? s0.replace(" ", "T") : s0;
        return `${isoLike}Z`;
      }

      return s0;
    } catch {
      return String(ts || "");
    }
  };

  const fmtDateTime = (iso) => {
    try {
      if (!iso) return "";
      const d = new Date(normalizeTimestampForLocalDisplay(iso));
      if (Number.isNaN(d.getTime())) return String(iso || "");
      return d.toLocaleString();
    } catch {
      return String(iso || "");
    }
  };

  const fmtDateOnly = (isoOrDate) => {
    try {
      if (!isoOrDate) return "";
      const s = String(isoOrDate || "").trim();
      if (!s) return "";
      // ISO datetime -> ISO date
      if (s.indexOf("T") !== -1) return s.split("T")[0];
      if (s.indexOf(" ") !== -1) return s.split(" ")[0];
      // Already yyyy-mm-dd
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return s;
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    } catch {
      return String(isoOrDate || "");
    }
  };

  const fmtDateTimeParts = (iso) => {
    try {
      if (!iso) return { date: "", time: "" };
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return { date: String(iso || ""), time: "" };
      return { date: d.toLocaleDateString(), time: d.toLocaleTimeString() };
    } catch {
      return { date: String(iso || ""), time: "" };
    }
  };

  const getProjectCode = (req) => {
    try {
      const src = String(
        req?.bundle_zip_url ||
          req?.translated_zip_filename ||
          req?.filename ||
          req?.translated_zip_url ||
          ""
      );
      const mm = src.match(/(\d{8}-\d{6})/);
      if (mm) return mm[1];

      // Fallback: derive a stamp from created_at (UTC) if present.
      const created = req?.created_at;
      if (created) {
        const d = new Date(created);
        if (!Number.isNaN(d.getTime())) {
          const yyyy = String(d.getUTCFullYear());
          const mo = String(d.getUTCMonth() + 1).padStart(2, "0");
          const dd = String(d.getUTCDate()).padStart(2, "0");
          const hh = String(d.getUTCHours()).padStart(2, "0");
          const mi = String(d.getUTCMinutes()).padStart(2, "0");
          const ss = String(d.getUTCSeconds()).padStart(2, "0");
          return `${yyyy}${mo}${dd}-${hh}${mi}${ss}`;
        }
      }

      return "";
    } catch {
      return "";
    }
  };

  const fmtDateOnlyLocal = (isoOrDate) => {
    try {
      if (!isoOrDate) return "";
      const s = String(isoOrDate || "").trim();
      if (!s) return "";

      // If we already have yyyy-mm-dd, build a local Date without timezone shifts.
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        const parts = s.split("-");
        const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
        if (Number.isNaN(d.getTime())) return s;
        return d.toLocaleDateString();
      }

      // ISO datetime -> Date
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return s;
      return d.toLocaleDateString();
    } catch {
      return String(isoOrDate || "");
    }
  };

  const addBusinessDays = (dateStr, daysToAdd) => {
    try {
      const base = fmtDateOnly(dateStr);
      if (!base) return "";
      const parts = base.split("-");
      if (parts.length !== 3) return base;
      let d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      let remaining = Number(daysToAdd || 0);
      if (Number.isNaN(remaining) || remaining <= 0) return base;

      while (remaining > 0) {
        d.setDate(d.getDate() + 1);
        const day = d.getDay(); // 0 Sun, 6 Sat
        if (day !== 0 && day !== 6) remaining -= 1;
      }

      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    } catch {
      return fmtDateOnly(dateStr);
    }
  };

  const computeAdjustedEstCompletionDate = (baseDateStr, submittedAtIso) => {
    const base = fmtDateOnly(baseDateStr);
    if (!base) return "";
    try {
      if (!submittedAtIso) return base;
      const submitted = new Date(submittedAtIso);
      if (Number.isNaN(submitted.getTime())) return base;

      const now = new Date();
      // Full days since submission (local time)
      const msPerDay = 24 * 60 * 60 * 1000;
      const days = Math.floor((now.getTime() - submitted.getTime()) / msPerDay);
      if (!days || days <= 0) return base;

      return addBusinessDays(base, days);
    } catch {
      return base;
    }
  };


  const mapStatusForUser = (status) => {
    const s = String(status || "").trim();
    if (s === "Open Request") return "Quote in Process";
    if (s === "Cost Submitted") return "Ready for Approval";
    if (s === "Sent to Ops") return "Project Started";
    return s || "—";
  };

  const normalizeCostLine = (line) => {
    if (!line) return "";
    if (typeof line === "string") {
      return line.replace(/^\s*[•\-]+\s*/g, "").trim();
    }
    if (typeof line === "object") {
      const label = line.label || line.name || line.title || "";
      const display = line.display || "";
      const value = line.value ?? line.amount ?? line.cost ?? "";
      const labelStr = String(label || "").trim();
      const displayStr = String(display || "").trim();
      const valueStr = String(value || "").trim();

      if (labelStr && displayStr) return `${labelStr}: ${displayStr}`;
      if (labelStr && valueStr) return `${labelStr}: ${valueStr}`;
      return labelStr || displayStr || valueStr || "";
    }
    return String(line).trim();
  };

  const getCostLinesAndTotal = (req) => {
    const normalizeToArray = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val;
      if (typeof val === "string") {
        // Allow newline-separated strings.
        return String(val)
          .split(/\r?\n/g)
          .map((s) => String(s || "").trim())
          .filter((s) => !!s);
      }
      return [val];
    };

        // Prefer final, then estimated, then legacy aliases.
    const finalArr = normalizeToArray(req?.final_cost_breakdown);
    const estArr = normalizeToArray(req?.estimated_cost_breakdown);
    const legacyArr = normalizeToArray(req?.cost_breakdown);

    const total = req?.final_total || req?.estimated_total || req?.total || "";

    const normalizeLines = (arr) =>
      arr
        .map((l) => normalizeCostLine(l))
        .filter((s) => !!s)
        // Avoid duplicating Total if it is already in the breakdown.
        .filter((s) => !/^total\s*:/i.test(s));

    // Some responses include a non-empty final_cost_breakdown, but the line objects may not be displayable.
    // If final normalizes to zero visible lines, fall back to estimated/legacy.
    const normalizedFinal = finalArr.length ? normalizeLines(finalArr) : [];
    const normalizedEst = estArr.length ? normalizeLines(estArr) : [];
    const normalizedLegacy = legacyArr.length ? normalizeLines(legacyArr) : [];

    const normalized =
      (normalizedFinal.length ? normalizedFinal : null) ||
      (normalizedEst.length ? normalizedEst : null) ||
      (normalizedLegacy.length ? normalizedLegacy : null) ||
      [];

    return { lines: normalized, total };

  };

    const formatMoney = (v) => {
    try {
      const n = Number(String(v ?? "").replace(/[$,]/g, "").trim());
      if (!Number.isFinite(n)) return "";
      return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } catch {
      return "";
    }
  };

  const getEstimatedRangeText = (req) => {
    try {
      const statusRaw = String(req?.status || "").trim();
      if (statusRaw !== "Open Request") return "";
      const totalRaw = req?.estimated_total || req?.total || req?.final_total || "";
      const totalNum = Number(String(totalRaw || "").replace(/[$,]/g, "").trim());
      if (!Number.isFinite(totalNum) || totalNum <= 0) return "";
      return `${formatMoney(totalNum * 0.90)} — ${formatMoney(totalNum * 1.25)}`;
    } catch {
      return "";
    }
  };

  const applyCostLabelShortcuts = (s0) => {
    try {
      let s = String(s0 || "");
      if (!s) return "";
      s = s.replace(/Estimated\s+Limited\s+Review\s*:/gi, "Est. Limited Review:");
      s = s.replace(/Estimated\s+Full\s+Human\s+Review\s*:/gi, "Est. Full Human Review:");
      s = s.replace(/Estimated\s+Post-Translation\s+Formatting\/QC\s*:/gi, "Est. Formatting/QC:");
      return s;
    } catch {
      return String(s0 || "");
    }
  };

const renderCostLines = (req) => {
    const estimatedRange = getEstimatedRangeText(req);
    if (estimatedRange) {
      return (
        <div className="text-sm leading-5">
          <div className="font-semibold">Estimated Cost:</div>
          <div>{estimatedRange}</div>
        </div>
      );
    }

    const { lines, total } = getCostLinesAndTotal(req);
    if (!lines.length && !total) return <span className="text-gray-500">—</span>;

    return (
      <div className="text-sm leading-5">
        <ul className="list-disc pl-5">
          {lines.map((line, idx) => (
            <li key={idx}>{applyCostLabelShortcuts(line)}</li>
          ))}
        </ul>
        {total ? (
          <div className="mt-1">
            <span className="font-semibold">Total:</span> {total}
          </div>
        ) : null}
      </div>
    );
  };

  // Row background colors by status/decision (Project Requests table only)
  const hexToRgba = (hex, alpha) => {
    const clean = String(hex || "").replace("#", "").trim();
    if (clean.length !== 6) return undefined;
    const r = parseInt(clean.slice(0, 2), 16);
    const g = parseInt(clean.slice(2, 4), 16);
    const b = parseInt(clean.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const getRowStyleForStatus = (statusRaw, userDecision) => {
    const s = String(statusRaw || "");
    const d = String(userDecision || "");
    if (d === "accepted" || s === "Approved") {
      return { backgroundColor: hexToRgba("#94c83d", 0.18) };
    }
    if (d === "rejected" || s === "Rejected") {
      return { backgroundColor: "rgba(156, 163, 175, 0.22)" };
    }
    if (s === "Cost Submitted") {
      // Ready for Approval
      return { backgroundColor: "rgba(249, 115, 22, 0.18)" }; // orange
    }
    if (s === "Open Request") {
      // Quote in Process
      return { backgroundColor: hexToRgba("#f5b800", 0.18) }; // yellow
    }
    return undefined;
  };


  const renderBullets = (arr) => {
    const list = Array.isArray(arr) ? arr.filter(Boolean) : [];
    if (!list.length) return <span className="text-gray-500">—</span>;

    return (
      <ul className="list-disc pl-5">
        {list.map((v, idx) => (
          <li key={idx}>{v}</li>
        ))}
      </ul>
    );
  };

  const activeLocalReq = useMemo(() => {
    if (!quoteTaskId) return null;
    return items.find((r) => String(r.task_id || "") === String(quoteTaskId)) || null;
  }, [items, quoteTaskId]);

  const quoteViewData = useMemo(() => {
    // If we have public quote data, use it.
    if (quoteToken && quoteData) {
      // If final breakdown is missing (still open), fallback to estimated from list.
      const hasFinal = Array.isArray(quoteData.final_cost_breakdown) && quoteData.final_cost_breakdown.length > 0;
      if (hasFinal) return quoteData;

      const fallback = activeLocalReq;
      if (!fallback) return quoteData;

      return {
        ...quoteData,
        estimated_cost_breakdown: fallback.estimated_cost_breakdown || fallback.cost_breakdown || [],
        estimated_total: fallback.estimated_total || fallback.total || "",
        source_filenames: fallback.source_filenames || quoteData.source_filenames,
        languages: fallback.languages || quoteData.languages,
      };
    }

    // Otherwise, render local request estimate.
    return activeLocalReq;
  }, [quoteToken, quoteData, activeLocalReq]);

  const quoteFiles = useMemo(() => {
    const files = Array.isArray(quoteViewData?.source_filenames) ? quoteViewData.source_filenames : [];
    return files;
  }, [quoteViewData]);

  const quoteLangs = useMemo(() => {
    const langs = Array.isArray(quoteViewData?.languages) ? quoteViewData.languages : [];
    return langs;
  }, [quoteViewData]);

  const quoteCosts = useMemo(() => {
    const primary = quoteViewData ? getCostLinesAndTotal(quoteViewData) : { lines: [], total: "" };
    if ((primary.lines && primary.lines.length) || primary.total) return primary;

    // Fallback: use the same fields as the list row when the quote payload is not displayable.
    if (activeLocalReq) return getCostLinesAndTotal(activeLocalReq);

    return primary;
    // eslint-disable-next-line
  }, [quoteViewData, activeLocalReq]);

  if (!user) {
    return (
      <div className="p-6 text-center text-gray-700">
        Please log in to view your project requests.
      </div>
    );
  }

  // Quote view (inline)

  const sortedItems = useMemo(() => {
    const arr = Array.isArray(items) ? [...items] : [];
    const dirMul = sortDir === "asc" ? 1 : -1;

    const safeStr = (v) => String(v ?? "").toLowerCase();
    const safeTime = (v) => {
      try {
        const d = new Date(v);
        const t = d.getTime();
        return Number.isNaN(t) ? 0 : t;
      } catch {
        return 0;
      }
    };

    arr.sort((a0, b0) => {
      const a = a0 || {};
      const b = b0 || {};

      if (sortKey === "created") {
        const av = safeTime(a.created_at);
        const bv = safeTime(b.created_at);
        if (av < bv) return -1 * dirMul;
        if (av > bv) return 1 * dirMul;
        return 0;
      }

      if (sortKey === "status") {
        const av = safeStr(mapStatusForUser(a.status));
        const bv = safeStr(mapStatusForUser(b.status));
        return av.localeCompare(bv) * dirMul;
      }

      if (sortKey === "project") {
        const av = safeStr(getProjectCode(a));
        const bv = safeStr(getProjectCode(b));
        return av.localeCompare(bv) * dirMul;
      }

      if (sortKey === "action") {
        const aStatus = String(a.status || "");
        const bStatus = String(b.status || "");
        const aLabel = aStatus === "Open Request" ? "view estimated quote" : "view details";
        const bLabel = bStatus === "Open Request" ? "view estimated quote" : "view details";
        return safeStr(aLabel).localeCompare(safeStr(bLabel)) * dirMul;
      }

      return 0;
    });

    return arr;
    // eslint-disable-next-line
  }, [items, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (!key) return;
    setSortKey((prev) => {
      const nextKey = String(key);
      if (String(prev) !== nextKey) {
        // default direction: created desc, everything else asc
        setSortDir(nextKey === "created" ? "desc" : "asc");
        return nextKey;
      }
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return prev;
    });
  };

  if (quoteTaskId || quoteToken) {
    const statusRaw = quoteViewData?.status || activeLocalReq?.status || "";
    const statusForUser = mapStatusForUser(statusRaw);
    const isQuoteInProcess = statusRaw === "Open Request";
    const isReadyForApproval = statusRaw === "Cost Submitted";
    const decision = String(quoteViewData?.user_decision || "").toLowerCase();

    return (
      <div data-testid="project-request-quote-page" className="p-6">
        <button
          data-testid="project-request-back"
          type="button"
          onClick={closeQuote}
          className="underline mb-4"
          style={{ color: "#00a7e1" }}
        >
          ← Back to Project Requests
        </button>

        <div data-testid="project-request-quote-title" className="text-2xl font-semibold mb-4">Translation Quote</div>

        {quoteLoading && <div data-testid="project-request-quote-loading" className="text-gray-600">Loading...</div>}
        {!quoteLoading && quoteError && <div data-testid="project-request-quote-error" className="text-red-600">{quoteError}</div>}

        {!quoteLoading && !quoteError && quoteViewData && (
          <div data-testid="project-request-quote-card" className="border rounded p-4 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="font-semibold mb-1">Created</div>
                <div data-testid="project-request-created" className="text-gray-800">{fmtDateTime((quoteViewData && (quoteViewData.created_at || quoteViewData.created)) || (activeLocalReq && (activeLocalReq.created_at || activeLocalReq.created)))}</div>
              </div>
              <div>
                <div className="font-semibold mb-1">Status</div>
                <div data-testid="project-request-status" className="text-gray-800">{statusForUser}</div>
                {String(quoteViewData.status || "") === "Approved" && quoteViewData.estimated_completion_date && (
                  <div className="text-xs text-gray-700 mt-1">Est. Completion: {fmtDateOnlyLocal(String(quoteViewData.estimated_completion_date || ""))}</div>
                )}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div data-testid="project-request-files">
                <div className="font-semibold mb-1">Files</div>
                {renderBullets(quoteFiles)}
              </div>
              <div data-testid="project-request-instructions">
                <div className="font-semibold mb-1">Additional Instructions</div>
                {String((quoteViewData && quoteViewData.additional_instructions) || "").trim() ? (
                  <div className="text-gray-800 whitespace-pre-wrap">{String(quoteViewData.additional_instructions || "")}</div>
                ) : (
                  <span className="text-gray-500">—</span>
                )}
              </div>
            </div>

            <div data-testid="project-request-languages" className="mt-4">
              <div className="font-semibold mb-1">Languages</div>
              {renderBullets(quoteLangs)}
            </div>

            <div data-testid="project-request-cost-breakdown" className="mt-4">
              <div className="font-semibold mb-1">Cost Breakdown</div>
              {getEstimatedRangeText(quoteViewData || activeLocalReq) ? (
                <div className="text-base leading-6">
                  <div data-testid="project-request-estimated-cost-label" className="font-semibold">Estimated Cost:</div>
                  <div data-testid="project-request-estimated-cost-value">{getEstimatedRangeText(quoteViewData || activeLocalReq)}</div>
                </div>
              ) : (quoteCosts.lines.length || quoteCosts.total ? (
                <div className="text-base leading-6">
                  <ul className="list-disc pl-5">
                    {quoteCosts.lines.map((line, idx) => (
                      <li key={idx}>{line}</li>
                    ))}
                  </ul>
                  {quoteCosts.total ? (
                    <div data-testid="project-request-total" className="mt-1">
                      <span className="font-semibold">Total:</span> {quoteCosts.total}
                    </div>
                  ) : null}
                </div>
              ) : (
                <span className="text-gray-500">—</span>
              ))}
            
            <div data-testid="project-request-estimated-completion" className="mt-4">
              <div className="font-semibold mb-1">Estimated Completion Date</div>
              <div className="text-gray-800">
                {(() => {
                  const base = quoteViewData?.estimated_completion_date || activeLocalReq?.estimated_completion_date || "";
                  const submittedAt = quoteViewData?.submitted_to_user_at || activeLocalReq?.submitted_to_user_at || "";
                  const status = String(statusRaw || "");
                  if (!base) return "—";
                  // While waiting for approval, push the estimate by business days based on time in queue.
                  const effective = status === "Cost Submitted"
                    ? computeAdjustedEstCompletionDate(base, submittedAt)
                    : fmtDateOnly(base);
                  return effective ? fmtDateOnlyLocal(effective) : "—";
                })()}
              </div>
            </div>

</div>

            {isQuoteInProcess ? (
              <div data-testid="project-request-quote-in-process" className="mt-4 text-base font-semibold text-red-600">
                Your request is being reviewed. We'll email you when your quote is ready for approval.
              </div>
            ) : null}

            {isReadyForApproval && quoteToken ? (
              <div className="mt-5">
                {!showPaymentChoices ? (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      data-testid="project-request-accept-pay"
                      type="button"
                      className="px-4 py-2 rounded text-white font-semibold"
                      style={{ backgroundColor: "#00a7e1" }}
                      onClick={() => {
                        setShowPaymentChoices(true);
                        setPaymentError("");
                      }}
                      disabled={quoteLoading}
                    >
                      Accept &amp; Pay
                    </button>
                    <button
                      data-testid="project-request-reject"
                      type="button"
                      className="px-4 py-2 rounded border"
                      style={{ borderColor: "#00a7e1", color: "#00a7e1" }}
                      onClick={() => submitDecision("rejected")}
                      disabled={quoteLoading || decision === "rejected"}
                    >
                      Reject
                    </button>
                  </div>
                ) : (
                  <div data-testid="project-request-payment-choices" className="mt-1">
                    <div data-testid="project-request-payment-title" className="text-base font-semibold text-gray-800">
                      Choose a payment method:
                    </div>

                    {paymentError ? (
                      <div data-testid="project-request-payment-error" className="mt-2 text-sm font-semibold text-red-600">{paymentError}</div>
                    ) : null}

                    <div className="mt-3 flex flex-col sm:flex-row gap-3">
                      <button
                        data-testid="project-request-pay-card"
                        type="button"
                        className="px-4 py-2 rounded text-white font-semibold"
                        style={{ backgroundColor: "#00a7e1" }}
                        onClick={startProjectPaymentStripe}
                        disabled={quoteLoading}
                      >
                        Pay with Credit Card
                      </button>
                      <button
                        data-testid="project-request-pay-paypal"
                        type="button"
                        className="px-4 py-2 rounded border font-semibold"
                        style={{ borderColor: "#00a7e1", color: "#00a7e1" }}
                        onClick={startProjectPaymentPayPal}
                        disabled={quoteLoading}
                      >
                        Pay with PayPal
                      </button>
                      <button
                        data-testid="project-request-payment-back"
                        type="button"
                        className="px-4 py-2 rounded border"
                        onClick={() => {
                          setShowPaymentChoices(false);
                          setPaymentError("");
                        }}
                        disabled={quoteLoading}
                      >
                        Back
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : null}

            {decision === "accepted" ? (
              <div className="mt-4">
                <div className="text-base font-semibold text-gray-800">
                  Payment Received. Thank you! Your translation request has been started.
                </div>
                <button
                  type="button"
                  onClick={closeQuote}
                  className="mt-3 underline"
                  style={{ color: "#00a7e1" }}
                >
                  Back to Project Requests
                </button>
              </div>
            ) : null}

            {decision === "rejected" ? (
              <div data-testid="project-request-rejected-message" className="mt-4 text-base font-semibold text-gray-800">
                You have rejected this translation quote. If you'd like to make changes,{" "}
                <a
                  href="https://zabtranslation.com/contact.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                  style={{ color: "#00a7e1" }}
                >
                  please contact us
                </a>
                .
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  }

  // List view
  return (
    <div data-testid="project-requests-page" className="p-6">
      <div data-testid="project-requests-title" className="text-lg font-semibold mb-4">Custom Project Requests</div>

      {loading && <div data-testid="project-requests-loading" className="text-gray-600">Loading...</div>}

      {!loading && error && <div data-testid="project-requests-error" className="text-red-600">{error}</div>}

      {!loading && !error && items.length === 0 && (
        <div data-testid="project-requests-empty" className="text-gray-600">No project requests found.</div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="w-full">
          {/* Top horizontal scrollbar (mirrors the table scroll) */}
          <div
            ref={tableScrollTopRef}
            className="overflow-x-auto"
            style={{ height: 16 }}
            onScroll={(e) => {
              try {
                if (syncingScrollRef.current) return;
                syncingScrollRef.current = true;
                const sl = e?.currentTarget?.scrollLeft || 0;
                if (tableScrollWrapRef.current) tableScrollWrapRef.current.scrollLeft = sl;
              } catch {
                // noop
              } finally {
                syncingScrollRef.current = false;
              }
            }}
          >
            <div style={{ width: tableScrollWidth || 0, height: 1 }} />
          </div>

          <div
            ref={tableScrollWrapRef}
            className="overflow-x-auto"
            onScroll={(e) => {
              try {
                if (syncingScrollRef.current) return;
                syncingScrollRef.current = true;
                const sl = e?.currentTarget?.scrollLeft || 0;
                if (tableScrollTopRef.current) tableScrollTopRef.current.scrollLeft = sl;
              } catch {
                // noop
              } finally {
                syncingScrollRef.current = false;
              }
            }}
          >
            <table data-testid="project-requests-table" ref={tableRef} className="min-w-full text-sm border">
              <thead className="bg-gray-100">
                <tr>
                  <th
                    className="text-left p-2 border w-28 sticky top-0 bg-gray-100 z-10 cursor-pointer select-none"
                    onClick={() => toggleSort("project")}
                    title="Sort by Project"
                  >
                    Project{sortKey === "project" ? (sortDir === "asc" ? " ▲" : " ▼") : " ↕"}
                  </th>
                  <th
                    className="text-left p-2 border w-28 sticky top-0 bg-gray-100 z-10 cursor-pointer select-none"
                    onClick={() => toggleSort("created")}
                    title="Sort by Created"
                  >
                    Created{sortKey === "created" ? (sortDir === "asc" ? " ▲" : " ▼") : " ↕"}
                  </th>
                  <th className="text-left p-2 border sticky top-0 bg-gray-100 z-10">Files</th>
                  <th className="text-left p-2 border sticky top-0 bg-gray-100 z-10">Languages</th>
                  <th
                    className="text-left p-2 border w-32 sticky top-0 bg-gray-100 z-10 cursor-pointer select-none"
                    onClick={() => toggleSort("status")}
                    title="Sort by Status"
                  >
                    Status{sortKey === "status" ? (sortDir === "asc" ? " ▲" : " ▼") : " ↕"}
                  </th>
                  <th className="text-left p-2 border sticky top-0 bg-gray-100 z-10">Cost Breakdown</th>
                  <th
                    className="text-left p-2 border sticky top-0 bg-gray-100 z-10 cursor-pointer select-none"
                    onClick={() => toggleSort("action")}
                    title="Sort by Action"
                  >
                    Action{sortKey === "action" ? (sortDir === "asc" ? " ▲" : " ▼") : " ↕"}
                  </th>
                </tr>
              </thead>
            <tbody>
              {sortedItems.map((req) => {
                const files = Array.isArray(req.source_filenames) ? req.source_filenames : [];
                const langs = Array.isArray(req.languages) ? req.languages : [];
                const statusRaw = String(req.status || "");
                const statusForUser = mapStatusForUser(statusRaw);

                const projectCode = getProjectCode(req);
                const isRejectedRow = (statusRaw === "Rejected") || (String(req.user_decision || "").toLowerCase() === "rejected");

                const createdParts = fmtDateTimeParts(req.created_at);

                const canView = true;
                const isQuoteInProcess = statusRaw === "Open Request";
                const actionLabel = isQuoteInProcess ? "View Estimated Quote" : "View Details";

                return (
                  <tr data-testid="project-requests-row" key={req.task_id || req.id} className={`border-t ${isRejectedRow ? "text-gray-700" : ""}`} style={getRowStyleForStatus(statusRaw, req.user_decision)}>
                    <td className="p-2 border align-top whitespace-nowrap w-28">
                      <div>{projectCode || "—"}</div>
                    </td>
<td className="p-2 border align-top whitespace-nowrap w-28">
                      <div>{createdParts.date}</div>
                      <div>{createdParts.time}</div>
                    </td>
                    <td className="p-2 border align-top">{renderBullets(files)}</td>
                    <td className="p-2 border align-top">{renderBullets(langs)}</td>
                    <td className="p-2 border align-top w-32">
                      <div>{statusForUser}</div>
                      {statusRaw === "Approved" && req.estimated_completion_date && (
                        <div className="text-xs text-gray-700 mt-1">
                        <div>Est. Completion:</div>
                        <div>{fmtDateOnlyLocal(String(req.estimated_completion_date || ""))}</div>
                      </div>
                      )}
                    </td>
                    <td className="p-2 border align-top">{renderCostLines(req)}</td>
                    <td className="p-2 border align-top whitespace-nowrap">
                      <button
                        data-testid="project-requests-action"
                        className={`px-4 py-2 rounded text-white font-semibold text-sm min-w-[170px] text-center ${canView ? "" : "opacity-50 cursor-not-allowed"}`}
                        style={{ backgroundColor: "#00a7e1" }}
                        onClick={() => openQuoteForRequest(req)}
                        disabled={!canView}
                        title={isQuoteInProcess ? "View estimated quote" : "View quote"}
                      >
                        {actionLabel}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}